import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Box from '@mui/material/Box';
import type { FC } from 'react';
import { useEdrGlobalFiltersAccess } from 'src/mindtime/hooks/useEdrGlobalFiltersAccess';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import { SecurityClientFilter } from './SecurityClientFilter';
import { SecurityEndpointFilter } from './SecurityEndpointFilter';
import { SecurityToolbarFilter } from './securityDashboardToolbar';

/** Fixed width for header All Clients / Endpoint / Date Range pills (keeps Export alignment stable). */
export const EDR_GLOBAL_FILTER_WIDTH = 160;

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

export type EdrGlobalScopeFiltersProps = {
  /**
   * Narrows endpoint options (and overrides global client for that purpose).
   * Defaults to session `selectedClientId`. Use a locked org id on client-scoped Endpoints.
   */
  pageOrganizationId?: string;
  /** Hide All Clients filter (e.g. Endpoints locked to one client). */
  hideOrganization?: boolean;
  /** Called after All Clients, Endpoint, or Date Range changes (e.g. reset table page). */
  onChange?: () => void;
  multipleEndpoints?: boolean;
};

/**
 * Session-global All Clients + Endpoint + Date Range filters for EDR page headers
 * (same client filter as EDR dashboard, without Search; render beside Export in `EdrPageHeader` actions).
 */
export const EdrGlobalScopeFilters: FC<EdrGlobalScopeFiltersProps> = ({
  pageOrganizationId,
  hideOrganization = false,
  onChange,
  multipleEndpoints = true,
}) => {
  const {
    dateRange,
    setDateRange,
    selectedClientId,
    setSelectedClientId,
    selectedEndpointId,
    setSelectedEndpointId,
    isReseller,
  } = useWazuhScope();
  const { canDateRange, canEndpoint, canOrganization } = useEdrGlobalFiltersAccess();

  const showClientFilter = !hideOrganization && isReseller && canOrganization;
  const endpointOrgId = pageOrganizationId ?? selectedClientId ?? undefined;

  if (!showClientFilter && !canEndpoint && !canDateRange) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
      {showClientFilter ? (
        <Box style={{ width: EDR_GLOBAL_FILTER_WIDTH, minWidth: EDR_GLOBAL_FILTER_WIDTH }}>
          <SecurityClientFilter
            value={selectedClientId}
            onChange={(clientId) => {
              setSelectedClientId(clientId);
              onChange?.();
            }}
            fullWidth
          />
        </Box>
      ) : null}
      {canEndpoint ? (
        <SecurityEndpointFilter
          value={selectedEndpointId}
          onChange={(endpointId) => {
            setSelectedEndpointId(endpointId);
            onChange?.();
          }}
          pageOrganizationId={endpointOrgId}
          pillLabel="Endpoint"
          allLabel="All Endpoint"
          searchPlaceholder="Search endpoint..."
          emptyMessage="No endpoint found"
          selectedFallbackLabel="Selected endpoint"
          showFilterIcon
          fullWidth={false}
          minWidth={EDR_GLOBAL_FILTER_WIDTH}
          multiple={multipleEndpoints}
        />
      ) : null}
      {canDateRange ? (
        <SecurityToolbarFilter
          label="Date Range"
          icon={<CalendarTodayIcon style={{ fontSize: 14 }} />}
          options={DATE_RANGE_OPTIONS}
          value={dateRange}
          minWidth={EDR_GLOBAL_FILTER_WIDTH}
          showAllOption={false}
          onChange={(value) => {
            setDateRange(value as WazuhDateRangeOption);
            onChange?.();
          }}
        />
      ) : null}
    </Box>
  );
};
